import { ThemedSkeleton, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListCategory, ListQuest } from '../../client/types';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { getColor } from '../../helpers/color';
import { getToday } from '../../helpers/datetime';
import { getCategorySummaries } from '../../helpers/quest';
import { useDailyProgress } from '../../hooks/useDailyProgress';
import { todayQuestProgressPath } from '../../navigation/paths';
import { CategoryIcon } from '../common/CategoryIcon';
import { CommonIcon } from '../common/CommonIcon';
import { groupQuestsByTimeOfDay } from './QuestDay';

export type QuestHeaderTodayProps = {
    quests: ListQuest[];
    categoryMap: Record<string, ListCategory>;
};

export const QuestHeaderToday = ({ quests, categoryMap }: QuestHeaderTodayProps) => {
    const navigate = useNavigate();
    const ritualsEnabled = useFeatureFlag('rituals');
    const { t } = useTranslation('common');

    const questsGroupBy = useMemo(() => groupQuestsByTimeOfDay(quests, getToday(), ritualsEnabled), [quests, ritualsEnabled]);

    const { data: dailyProgressData, loading } = useDailyProgress(questsGroupBy);

    const doneSum = dailyProgressData ? Object.values(dailyProgressData.progressMap).reduce((sum, p) => sum + p.done, 0) : 0;
    const totalSum = dailyProgressData ? Object.values(dailyProgressData.progressMap).reduce((sum, p) => sum + p.total, 0) : 0;
    const summaryPercentage = totalSum > 0 ? Math.round((doneSum / totalSum) * 100) : 0;

    const categorySummaries = useMemo(
        () => getCategorySummaries(dailyProgressData?.completedQuests ?? [], categoryMap),
        [dailyProgressData?.completedQuests, categoryMap],
    );
    const topFour = categorySummaries.slice(0, 4);
    const moreCount = Math.max(categorySummaries.length - 4, 0);

    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick } = useLiquidGlassPress({
        preventDefault: true,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            navigate(todayQuestProgressPath);
        },
    });

    return (
        <Box
            component="button"
            type="button"
            aria-label={t('quest.viewProgress')}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                m: 0,
                p: '10px 14px',
                gap: '8px',
                border: 'none',
                borderBottom: '1px solid var(--tg-theme-section-bg-color)',
                background: 'none',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' : 'transparent',
                transition: 'background-color 0.12s ease',
            }}
        >
            {loading ? (
                <ThemedSkeleton variant="text" width={150} height={16} />
            ) : (
                <Caption
                    style={{
                        minWidth: 0,
                        overflow: 'hidden',
                        fontSize: '13px',
                        color: 'var(--tgui--hint_color)',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {t('quest.progress', { done: doneSum, total: totalSum, percentage: summaryPercentage })}
                </Caption>
            )}
            <Stack direction="row" alignItems="center" gap="4px" flexShrink={0}>
                {!loading && (
                    <>
                        {topFour.map(c => (
                            <Box
                                key={c.categoryId}
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 0.75,
                                    background: getColor(c.categoryColor),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CategoryIcon
                                    icon={c.categoryIcon}
                                    iconColor={c.categoryColor}
                                    customIcon={c.categoryCustomIcon}
                                    type="filled"
                                    size={9}
                                    color="var(--tg-theme-text-color)"
                                />
                            </Box>
                        ))}
                        {moreCount > 0 && (
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 0.75,
                                    background: 'var(--tgui--hint_color)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Caption style={{ fontSize: '7px', fontWeight: 'bold' }}>+{moreCount}</Caption>
                            </Box>
                        )}
                    </>
                )}
                <CommonIcon icon="chevron_right" type="filled" size={16} color="var(--tgui--hint_color)" />
            </Stack>
        </Box>
    );
};
